import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, ArrowRight, ShieldOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DashboardSummary, type DashboardSummaryData, type DashboardRun, type TrendPoint } from '@/components/dashboard/dashboard-summary';
import { PortfoliosSection, type PortfolioCardData } from '@/components/dashboard/portfolios-section';
import type { FindingCounts } from '@nirvik/shared';

interface DashboardData {
  healthScore: number;
  totalProjects: number;
  projectsScanned: number;
  totalRuns: number;
  defaultPortfolioId: string | null;
  portfolios: PortfolioCardData[];
  findingCounts: FindingCounts;
  recentRuns: DashboardRun[];
  findingTrends: TrendPoint[];
}

export function DashboardPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { data: dashRes, isLoading } = useQuery({
    queryKey: ['org', orgId, 'dashboard'],
    queryFn: () => apiClient.getDashboardSummary(orgId!),
    enabled: !!orgId,
  });

  const data = (dashRes?.data as DashboardData | undefined) || {
    healthScore: 0,
    totalProjects: 0,
    projectsScanned: 0,
    totalRuns: 0,
    defaultPortfolioId: null,
    portfolios: [],
    findingCounts: { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 },
    recentRuns: [],
    findingTrends: [],
  };

  // Day-one CTA target: drop straight into the project-create form in the
  // org's default portfolio (auto-created on org creation). Falls back to
  // the portfolio list if the default isn't loaded yet.
  const newProjectHref = data.defaultPortfolioId
    ? `/orgs/${orgId}/portfolios/${data.defaultPortfolioId}/projects/new`
    : `/orgs/${orgId}/portfolios`;

  const summary: DashboardSummaryData = {
    healthScore: data.healthScore,
    totalProjects: data.totalProjects,
    totalRuns: data.totalRuns,
    findingCounts: data.findingCounts,
    recentRuns: data.recentRuns,
    findingTrends: data.findingTrends,
  };

  const hasProjects = data.totalProjects > 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 dark:bg-muted rounded w-40 mb-2" />
            <div className="h-4 bg-gray-100 dark:bg-muted rounded w-56" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-3 p-6 animate-pulse">
            <div className="h-32 bg-gray-100 dark:bg-muted rounded-full w-32 mx-auto" />
          </Card>
          <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-4 animate-pulse">
                <div className="h-10 bg-gray-100 dark:bg-muted rounded w-10 mb-3" />
                <div className="h-6 bg-gray-200 dark:bg-muted rounded w-12 mb-1" />
                <div className="h-4 bg-gray-100 dark:bg-muted rounded w-20" />
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!hasProjects) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Your security overview at a glance</p>
        </div>

        <Card className="p-12 flex flex-col items-center text-center gap-6 border-dashed">
          <div className="h-16 w-16 rounded-2xl bg-gray-100 dark:bg-muted flex items-center justify-center">
            <ShieldOff className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-2">No projects yet</h2>
            <p className="text-muted-foreground max-w-md">
              Your security score will appear here once you add a project and run your first agent.
              We've already created a default portfolio for you — just add a website or repository to get started.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={newProjectHref}>
              <Button size="lg">
                <Plus className="h-4 w-4 mr-2" />
                Add your first project
              </Button>
            </Link>
            <Link to={`/orgs/${orgId}/portfolios`}>
              <Button size="lg" variant="outline">
                Manage portfolios
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Your security overview at a glance</p>
        </div>
        <Link to={newProjectHref}>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </Link>
      </div>

      <DashboardSummary
        orgId={orgId!}
        drillHref={`/orgs/${orgId}/portfolios`}
        runHref={(run) =>
          run.portfolioId
            ? `/orgs/${orgId}/portfolios/${run.portfolioId}/projects/${run.projectId}/agents/runs/${run.runId}`
            : null
        }
        data={summary}
        beforeRecentRuns={<PortfoliosSection orgId={orgId!} portfolios={data.portfolios} />}
      />
    </div>
  );
}
